import i18n from "i18next";
import { createRef } from "react";
import { USER_SETTINGS } from "../../../../../constants/UserConstants";
import { SESSION_STORAGE_KEYS, ssSet } from "../../../../../helpers/sessionStorage";
import { IRootStore } from "../../../../../mobx/root-store";
import { TourConfig } from "../../../../../tours/types";
import FeedbackIntroductionContent from "./FeedbackIntroductionContent/FeedbackIntroductionContent";

/**
 * Refs for TG Evaluation tour anchors.
 * Components should import and assign these refs to their elements.
 */
export const tgEvaluationTourRefs = {
	modelDropdown: createRef<HTMLDivElement>(),
	compareModels: createRef<HTMLDivElement>(),
	promptInput: createRef<HTMLDivElement>(),
	resultImage: createRef<HTMLElement>(),
	influenceSlider: createRef<HTMLDivElement>(),
};

/**
 * Creates the TG Evaluation tour configuration.
 * Pass this directly to tourStore.start()
 *
 * Note: This is a function (not a constant) to ensure translations are resolved
 * at runtime when the tour starts, not at module load time.
 */
export const getTgEvaluationTour = (): TourConfig => {
	// Translation helpers - called at runtime to get current language
	const t = (key: string) => i18n.t(key, { ns: "tg" });
	const pt = (key: string) => i18n.t(`playground.${key}`, { ns: "translation" });

	return {
		name: "tg-evaluation",
		spotlightClicks: true,
		steps: [
			{
				targetRef: tgEvaluationTourRefs.modelDropdown,
				title: t("models.viewer.evaluation.onboarding.tutorialSteps.selectModel.title"),
				content: t("models.viewer.evaluation.onboarding.tutorialSteps.selectModel.content"),
				disableBeacon: true,
				disableScrolling: true,
			},
			{
				targetRef: tgEvaluationTourRefs.compareModels,
				title: t("models.viewer.evaluation.onboarding.tutorialSteps.compareModels.title"),
				content: t("models.viewer.evaluation.onboarding.tutorialSteps.compareModels.content"),
				disableBeacon: true,
				disableScrolling: true,
			},
			{
				targetRef: tgEvaluationTourRefs.promptInput,
				title: t("models.viewer.evaluation.onboarding.tutorialSteps.addPrompt.title"),
				content: t("models.viewer.evaluation.onboarding.tutorialSteps.addPrompt.content"),
				disableBeacon: true,
				locale: {
					last: t("models.viewer.evaluation.onboarding.tutorialSteps.addPrompt.locale.last"),
				},
			},
			// The result image doesn't exist until the user submits a prompt and generation completes.
			// The tour automatically pauses when advancing to this step (since resultImage ref is null).
			// Once generation finishes, PlaygroundImages.tsx sets the ref and calls tourStore.resume().
			{
				targetRef: tgEvaluationTourRefs.resultImage,
				title: pt("feedbackIntroduction.title"),
				content: <FeedbackIntroductionContent />,
				disableBeacon: true,
				locale: {
					last: pt("feedbackIntroduction.buttonText"),
				},
			},
			{
				targetRef: tgEvaluationTourRefs.influenceSlider,
				title: pt("tgInfluenceIntroduction.title"),
				content: pt("tgInfluenceIntroduction.content"),
				disableBeacon: true,
				locale: {
					last: "Finish",
				},
			},
		],
		onComplete: async (store: IRootStore) => {
			await store.authStore.user?.updateSettings(USER_SETTINGS.hideTgEvaluationTutorial, true);
			await store.authStore.user?.refreshSettings();
			store.tgStore.tgModelPlaygroundStore.isTutorialActive = false;
		},
		onSkip: async (store: IRootStore) => {
			store.tgStore.tgModelPlaygroundStore.isTutorialActive = false;
		},
	};
};

export const getTgEvaluationZeroPositiveFeedbackReminderTour = (): TourConfig => {
	const t = (key: string) =>
		i18n.t(`models.viewer.evaluation.feedbackGamification.zeroPositiveFeedbackReminder.${key}`, { ns: "tg" });

	return {
		name: "tg-evaluation-zero-positive-feedback-reminder",
		steps: [
			{
				targetRef: tgEvaluationTourRefs.resultImage,
				title: t("title"),
				content: t("content"),
				disableBeacon: true,
				locale: {
					last: t("done"),
				},
			},
		],
		onComplete: async (_) => {
			ssSet(SESSION_STORAGE_KEYS.feedbackReminderShown, "true");
		},
		onSkip: async (_) => {
			ssSet(SESSION_STORAGE_KEYS.feedbackReminderShown, "true");
		},
	};
};
